import { ProductPrice } from '@msdyn365-commerce/retail-proxy';
import React from 'react';
import { IGetSingleUnitOfMeasureConversionResponse } from '../../../actions/DataServiceEntities.g';

interface UomConversionNodeProps {
    productPrice: ProductPrice | null | undefined;
    uomConversionData: IGetSingleUnitOfMeasureConversionResponse | undefined;
    pricePerLabelText: string;
    unitPricePerLabelText: string;
    thousandText: string;
    displayedSurchargeLabel: string;
    maximumDecimalsEcommPrice?: number;
    maximumDecimalsFyiPrice?: number;
    className: string;
}

export const UomConversionNode: React.FC<UomConversionNodeProps> = ({
    productPrice,
    uomConversionData,
    pricePerLabelText,
    unitPricePerLabelText,
    thousandText,
    displayedSurchargeLabel,
    maximumDecimalsEcommPrice = 2,
    maximumDecimalsFyiPrice = 2,
    className
}) => {
    if (!uomConversionData) {
        return null;
    }

    const basePrice =
        productPrice?.CustomerContextualPrice ??
        productPrice?.AdjustedPrice ??
        productPrice?.TradeAgreementPrice ??
        productPrice?.BasePrice ??
        0;

    const calculateSurchargePerDefaultUnit = () => {
        const ecommSurchargeAmount = uomConversionData?.EcommSurchargeAmount;
        const ecommSurchargeFactor = uomConversionData?.EcommSurchargeFactor;
        const isEcommSurchargePercent = uomConversionData?.IsEcommSurchargePercent;

        if (ecommSurchargeAmount && ecommSurchargeFactor) {
            if (isEcommSurchargePercent) {
                return basePrice * (ecommSurchargeAmount / 100);
            } else {
                return ecommSurchargeAmount / ecommSurchargeFactor;
            }
        }
        return 0;
    };
    const surchargeAmountPerDefaultUnit = calculateSurchargePerDefaultUnit();
    const unitMultipliedSurchargeAmount = uomConversionData?.EcommPriceUnit
        ? uomConversionData.EcommPriceUnit * surchargeAmountPerDefaultUnit
        : surchargeAmountPerDefaultUnit;
    const unitMultipliedPrice = formatPricePerValues(
        unitMultipliedSurchargeAmount + (uomConversionData?.EcommPriceUnit ? uomConversionData.EcommPriceUnit * basePrice : basePrice),
        2,
        maximumDecimalsEcommPrice || 2
    );
    const fyiSurchargeAmount = uomConversionData?.FyiConversionFactor
        ? uomConversionData.FyiConversionFactor * surchargeAmountPerDefaultUnit
        : 0;
    const resFyiPrice = uomConversionData?.FyiConversionFactor ? uomConversionData.FyiConversionFactor * basePrice : 0;
    const fyiPrice = formatPricePerValues(fyiSurchargeAmount + resFyiPrice, 2, maximumDecimalsFyiPrice || 2);

    const pricePerText =
        uomConversionData?.DefaultDescription && uomConversionData?.DefaultDescription.toLocaleLowerCase().includes('per')
            ? pricePerLabelText.toLocaleLowerCase().replace('per', '')
            : pricePerLabelText;

    const unitPricePerText =
        uomConversionData?.FyiDescription && uomConversionData?.FyiDescription.toLocaleLowerCase().includes('per')
            ? unitPricePerLabelText.toLocaleLowerCase().replace('per', '')
            : unitPricePerLabelText;

    let unitNumDescriptor = '';
    if (uomConversionData?.EcommPriceUnit === 1000) {
        unitNumDescriptor = thousandText;
    } else if (uomConversionData?.EcommPriceUnit && uomConversionData.EcommPriceUnit > 1) {
        unitNumDescriptor = uomConversionData.EcommPriceUnit.toString();
    }

    return (
        <>
            {/* ─── “PER” BLOCK ─── */}
            <div className={`${className}__price-section-per`}>
                <div className={`${className}__price-section-per--text`}>
                    {`${pricePerText} ${unitNumDescriptor} ${uomConversionData.DefaultDescription}`}
                </div>
                <div className={`${className}__price-section-per--amount`}>{`$${unitMultipliedPrice}`}</div>
                {unitMultipliedSurchargeAmount > 0 && (
                    <div className={`${className}__price-section-per--surcharge`}>
                        {`${displayedSurchargeLabel} $${formatPricePerValues(unitMultipliedSurchargeAmount, 2, 2)}`}
                    </div>
                )}
            </div>

            {/* ─── “UNIT” BLOCK (FYI) ─── */}
            {(Number(fyiPrice) || 0) > 0 && (
                <div className={`${className}__price-section-unit`}>
                    <div className={`${className}__price-section-unit--text`}>
                        {`${unitPricePerText} ${uomConversionData.FyiDescription}`}
                    </div>
                    <div className={`${className}__price-section-unit--amount`}>{`$${fyiPrice}`}</div>
                    {fyiSurchargeAmount > 0 && (
                        <div className={`${className}__price-section-unit--surcharge`}>
                            {`${displayedSurchargeLabel} $${formatPricePerValues(fyiSurchargeAmount, 2, 2)}`}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default UomConversionNode;

export const formatPricePerValues = (value: number | undefined, minDecimals: number, maxDecimals: number): string | undefined => {
    if (!value) {
        return undefined;
    }
    // const options = {

    //     style: 'decimal',
    //     minimumFractionDigits: minDecimals,
    //     maximumFractionDigits: maxDecimals
    //     // minimumFractionDigits: 2,
    //     // maximumFractionDigits: 2
    //     // maximumFractionDigits: 5
    // };
    return value.toLocaleString('en-US', { style: 'decimal', minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals });
};
